import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Detects an Emergent Google OAuth return by looking at the URL fragment.
 * Wraps children — renders them unchanged when there is no callback in progress.
 * When #session_id=… is present, exchanges it with the backend, sets the cookie,
 * strips the hash, and renders the app normally.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function GoogleAuthCallback({ children }) {
  const location = useLocation();
  const nav = useNavigate();
  const { exchangeGoogleSession } = useAuth();
  const [processing, setProcessing] = useState(location.hash?.includes("session_id="));
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (!location.hash?.includes("session_id=")) return;
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const params = new URLSearchParams(location.hash.slice(1));
    const sessionId = params.get("session_id");
    if (!sessionId) { setProcessing(false); return; }

    (async () => {
      try {
        const u = await exchangeGoogleSession(sessionId);
        toast.success(`Welcome, ${u.name || u.email}`);
      } catch {
        toast.error("Google sign-in failed. Please try again.");
      }
      // Strip the fragment and land the user on the homepage
      window.history.replaceState({}, "", window.location.pathname);
      nav("/", { replace: true });
      setProcessing(false);
    })();
  }, [location.hash, exchangeGoogleSession, nav]);

  if (processing) {
    return (
      <div data-testid="google-auth-callback" className="min-h-screen flex items-center justify-center" style={{ background: "#efe7d7" }}>
        <div className="text-center text-ink">
          <p className="text-[10px] tracking-luxury uppercase text-goldDark mb-4">The Members' Door</p>
          <p className="font-editorial text-3xl">Opening your account…</p>
        </div>
      </div>
    );
  }
  return children;
}
